<template>
    <div>

        <PageHeader>
            {{ $t('invoice') }} {{ invoice?.number }}
            <template #side>
                <ButtonIcon :to="parentRoute">
                    {{ $t('goBack') }}
                </ButtonIcon>
            </template>
        </PageHeader>

        <Card overlay :loading="requestInvoice.getLoading()" class="height-min-200">

            <ErrorMessage v-if="!invoice" :value="requestInvoice.getError()" />

            <template v-if="invoice" #header>
                <div class="d-flex gap-3">
                    <span>{{ invoice.number }}</span>
                    <BadgeFinanceDocumentStatus :status="invoice.status" />
                </div>
            </template>

            <div v-if="invoice">

                <div class="my-4"></div>

                <div class="grid-template">

                    <div class="label">{{ $t('advertiser') }}:</div>
                    <div class="value">{{ invoice.advertiser.company }}</div>

                    <div class="label">{{ $t('issueDate') }}:</div>
                    <div class="value">{{ $format.dateAndHour(invoice.createdAt, 'dd-MM-yyyy') }}</div>

                    <div class="label">{{ $t('dueDate') }}:</div>
                    <div class="value">{{ $format.dateAndHour(invoice.dueDate, 'dd-MM-yyyy') }}</div>

                </div>

                <div class="my-5"></div>

                <DataTable
                    :pagination="false"
                    :fields="fields"
                    :items="invoice?.items || []"
                    sort-by="id"
                    sort-direction="DESC"
                    no-sort-reset
                    foot-clone
                    no-padding
                    small
                    :hover="false"
                >
                    <template #[`cell(campaign.name)`]="{index, value}">
                        {{ index+1 }}. {{ value }}
                    </template>
                    <template #cell(settlementNetAmount)="{item}">
                        {{ $format.currency(item.settlementNetAmount) }}
                    </template>
                    <template #cell(netAmount)="{item}">
                        {{ $format.currency(item.netAmount) }}
                    </template>
                    <template #cell(grossAmount)="{item}">
                        {{ $format.currency(item.grossAmount) }}
                    </template>
                    <template #foot()>
                        <div></div>
                    </template>
                    <template #[`foot(campaign.name)`]>
                        {{ $t('total2').toUpperCase() }}
                    </template>
                    <template #foot(settlementNetAmount)>
                        {{ $format.currency(invoice.settlementNetAmount) }}
                    </template>
                    <template #foot(netAmount)>
                        {{ $format.currency(invoice.netAmount) }}
                    </template>
                    <template #foot(grossAmount)>
                        {{ $format.currency(invoice.grossAmount) }}
                    </template>
                </DataTable>

                <div class="my-5"></div>

                <h5>{{ $t('toBePaid') }}: {{ $format.currency(invoice.paidAmount ? 0 : invoice.grossAmount) }}</h5>

                <div class="my-5"></div>

                <div class="d-flex gap-3">
                    <ButtonIcon
                        v-if="!invoice?.status"
                        :loading="requestInvoiceSettle.getLoading()"
                        hide-label-while-loading
                        :disabled="anyLoading"
                        @click="settleInvoice()"
                    >
                        {{ $t('settleInvoice') }}
                    </ButtonIcon>
                    <ButtonIcon
                        :loading="false"
                        hide-label-while-loading
                        :disabled="anyLoading"
                        variant="outline-primary"
                        :to="transactionsUrl"
                    >
                        {{ $t('showTransactions') }}
                    </ButtonIcon>
                    <ButtonIcon
                        v-if="isSuperAdmin"
                        class="ml-auto"
                        :loading="requestInvoiceAbandon.getLoading()"
                        hide-label-while-loading
                        variant="danger"
                        :disabled="anyLoading"
                        @click="abandonInvoice()"
                    >
                        {{ $t('abandonInvoice') }}
                    </ButtonIcon>
                </div>

            </div>

        </Card>

    </div>
</template>



<script>
    import BadgeFinanceDocumentStatus from '@/components/common/BadgeFinanceDocumentStatus.vue';
    import DataTable from '@/components/common/DataTable.vue';

    export default {
        name: 'Details',
        components: {
            BadgeFinanceDocumentStatus,
            DataTable,
        },
        data() {
            return {
                id: this.$route.params.id,
                requestInvoice: this.$req('advertiserSettlements/Invoices'),
                requestInvoiceSettle: this.$req('advertiserSettlements/InvoicesSettle'),
                requestInvoiceAbandon: this.$req('advertiserSettlements/InvoicesAbandon'),
            };
        },
        computed: {
            fields() {
                return [
                    { key: 'campaign.name', label: this.$t('itemsCampaigns'), tdClass: 'text-nowrap', thClass: '' },
                    { key: 'settlementNetAmount', label: this.$t('netSettlementAmount'), tdClass: 'text-right text-nowrap', thClass: 'text-right' },
                    { key: 'netAmount', label: this.$t('netInvoiceAmount'), tdClass: 'text-right text-nowrap', thClass: 'text-right' },
                    { key: 'grossAmount', label: this.$t('grossAmount'), tdClass: 'text-right text-nowrap', thClass: 'text-right' },
                ];
            },
            parentRoute() {
                return this.$route.params.parentUrl ? this.$router.resolve(this.$route.params.parentUrl).resolved : this.$route.matched.slice(-2)[0];
            },
            invoice() {
                return this.requestInvoice.getData();
            },
            isSuperAdmin() {
                return this.$store.getters['auth/isRoleSuperAdmin'];
            },
            transactionsUrl() {
                if (!this.invoice) return { name: 'ReportsTransactions' };
                const from = this.$format.date(this.invoice.createdAt, 'yyyy-MM-dd');
                const to = this.$format.date(new Date(), 'yyyy-MM-dd');
                const financeDocuments = this.invoice.items.map(item => item.id);
                return { name: 'ReportsTransactions', query: { from, to, financeDocuments } };
            },
            anyLoading() {
                return this.requestInvoice.getLoading() || this.requestInvoiceSettle.getLoading() || this.requestInvoiceAbandon.getLoading();
            },
        },
        methods: {
            fetchInvoice() {
                this.requestInvoice.get({ urlParams: { id: this.id } }).catch(error => !this.invoice && this.$toastr.error(error.message, this.$t('errorOccurred')));
            },
            settleInvoice() {
                this.requestInvoiceSettle
                    .put({ urlParams: { id: this.id } })
                    .then(this.fetchInvoice)
                    .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred')));
            },
            abandonInvoice() {
                this.requestInvoiceAbandon
                    .put({ urlParams: { id: this.id } })
                    .then(this.fetchInvoice)
                    .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred')));
            },
        },
        created() {
            this.fetchInvoice();
        },
    };
</script>



<style lang="scss" scoped>
    .grid-template {
        display: grid;
        grid-template-columns: minmax(60px, max-content) auto;
        column-gap: 1rem;
        row-gap: 0.4rem;
        align-items: center;

        .label {
            font-weight: bold;
        }
    }
</style>
